<template>
    <ion-page class="camera-preview-modal">
        <!-- Web platform shows a simple loading overlay while it redirects to file picker -->
        <div v-if="$platform === 'web'" class="loading-overlay">
            <p class="loading-spinner">
                <mdicon class="text-primary" name="loading" size="64" :spin="true"></mdicon>
            </p>
            <div class="loading-text">
                <ion-label class="text-dark">Afbeeldingskiezer wordt geladen...</ion-label>
            </div>
        </div>

        <!-- Native app camera UI - only shown on mobile platforms -->
        <template v-else>
            <div v-if="showFlash" class="flash-overlay"></div>

            <div v-if="loading_state === 'saving' || loading_state === 'starting'" class="loading-overlay">
                <p class="loading-spinner">
                    <mdicon class="text-primary" name="loading" size="64" :spin="true"></mdicon>
                </p>

                <div class="loading-text" @click="confirmBack()">
                    <ion-label class="text-dark">Een momentje...</ion-label>
                </div>
            </div>

            <ion-content id="camera">
                <div class="circled topleft" @click="confirmBack()" :class="$platform === 'ios' ? 'mt-4' : null">
                    <mdicon name="close" size="24"></mdicon>
                </div>

                <div class="circled topright" @click="settings" :class="$platform === 'ios' ? 'mt-4' : null">
                    <mdicon name="cogs" size="24"></mdicon>
                </div>

                <div class="custom-popover" v-if="show_popover" @click.self="closePopover">
                    <div class="popover-content">
                        <ion-list>
                            <ion-item>
                                <ion-label>Tijdstempel</ion-label>
                                <ion-toggle v-model="setTimeStamp"></ion-toggle>
                            </ion-item>
                            <ion-item>
                                <ion-label>Afb. opslaan in galerij</ion-label>
                                <ion-toggle v-model="setSaveInGallery"></ion-toggle>
                            </ion-item>
                            <ion-item v-if="$platform !== 'web'">
                                <ion-label>Lokale backup</ion-label>
                                <ion-toggle v-model="setSaveLocalBackup"></ion-toggle>
                            </ion-item>
                            <ion-item>
                                <ion-label>Inspecteur naam</ion-label>
                                <ion-toggle v-model="setInspectorName"></ion-toggle>
                            </ion-item>
                        </ion-list>
                    </div>
                </div>

                <div id="cameraPreview" class="finder">
                    <TransitionGroup name="fade" tag="div" class="image-preview-array-wrapper" style="bottom:15px">
                        <div v-for="(image) in image_urls" :key="image.uuid" class="image-preview-container">
                            <img :src="image.base64" alt="preview" />
                            <div class="centered-delete-badge" @click.stop="deleteImage(image)">
                                <mdicon name="close" size="16" class="text-danger small-icon"></mdicon>
                            </div>
                        </div>
                    </TransitionGroup>

                    <div class="right-align-buttons" v-if="screenOrientation === 'landscape'">
                        <div @click="flip()" class="circled" :class="$platform === 'ios' ? 'ios-button' : ''">
                            <mdicon v-if="loading_state !== 'reverse'" name="camera-front" size="24"></mdicon>
                            <mdicon v-else name="dots-circle" size="24" :spin="true"></mdicon>
                        </div>

                        <div v-if="hasFlash" @click="flashToggle()" class="circled" :class="[$platform === 'ios' ? 'ios-button' : '', flashMode === 'torch' ? 'background-on' : null]">
                            <mdicon v-if="flashMode === 'torch'" name="flashlight" size="24"></mdicon>
                            <mdicon v-else name="flashlight-off" size="24"></mdicon>
                        </div>

                        <div @click="takePicture()" class="large-circled" :class="$platform === 'ios' ? 'ios-button' : ''">
                            <mdicon v-if="loading_state !== 'idle'" name="dots-circle" size="48" :spin="true"></mdicon>
                        </div>

                        <div @click="getFromGallery()" class="circled" :class="$platform === 'ios' ? 'ios-button' : ''">
                            <mdicon name="image" size="32"></mdicon>
                        </div>

                        <div @click="closeAndSave()" color="primary" class="circled button-primary" :class="$platform === 'ios' ? 'ios-button' : ''">
                            <mdicon v-if="loading_state !== 'saving'" name="check-bold" size="32" class="text-custom-white"></mdicon>
                            <mdicon v-else name="loading" :spin="true" size="32"></mdicon>
                        </div>
                    </div>
                </div>
            </ion-content>

            <ion-footer v-if="screenOrientation === 'portrait'" :class="$platform === 'ios' ? 'ios-footer' : ''">
                <transition name="leveldown">
                    <div class="control-bar" :class="$platform === 'ios' ? 'ios-control-bar' : ''">

                        <div class="left-buttons">
                            <div @click="getFromGallery()" class="circled" :class="$platform === 'ios' ? 'ios-button' : ''">
                                <mdicon name="image" size="32"></mdicon>
                            </div>

                            <div @click="flip()" class="circled" :class="$platform === 'ios' ? 'ios-button' : ''">
                                <mdicon v-if="loading_state !== 'reverse'" name="camera-front" size="24"></mdicon>
                                <mdicon v-else name="dots-circle" size="24" :spin="true"></mdicon>
                            </div>
                        </div>

                        <div @click="takePicture()" class="large-circled absolute-center-button" :class="$platform === 'ios' ? 'ios-button' : ''">
                            <mdicon v-if="loading_state !== 'idle'" name="dots-circle" size="48" :spin="true"></mdicon>
                        </div>

                        <div class="right-buttons">
                            <div v-if="hasFlash" @click="flashToggle()" class="circled" :class="[$platform === 'ios' ? 'ios-button' : '', flashMode === 'torch' ? 'background-on' : null]">
                                <mdicon v-if="flashMode === 'torch'" name="flashlight" size="24"></mdicon>
                                <mdicon v-else name="flashlight-off" size="24"></mdicon>
                            </div>

                            <div @click="closeAndSave()" class="circled button-primary" :class="$platform === 'ios' ? 'ios-button' : ''">
                                <mdicon v-if="loading_state !== 'saving'" size="32" class="text-" name="check-bold"></mdicon>
                                <mdicon v-else name="loading" :spin="true" size="32"></mdicon>
                            </div>
                        </div>
                    </div>
                </transition>
            </ion-footer>
        </template>
    </ion-page>
</template>

<script>
import { CameraPreview } from "@capacitor-community/camera-preview";
import { Camera } from "@capacitor/camera";
import { v4 as uuidv4 } from "uuid";
import { modalController } from "@ionic/vue";
import { Directory, Filesystem } from "@capacitor/filesystem";
import { db } from "@/services/db";
import convert from "@/services/convert";
import { syncApi } from "@/services/index";
import imageFileManager from "@/utils/imageFileManager";
import mitt from "mitt";

export default {
    name: "CameraModal",
    props: {
        square: {
            type: Boolean,
            default: true,
        },
        upload_url: {
            type: String,
            required: true,
        },
        imageable_uuid: {
            type: String,
            required: true,
        },
    },
    data() {
        return {
            showFlash: false,
            image_urls: [],
            flashMode: "off",
            loading_state: "starting",
            show_message: true,
            handleBackButton: null,
            hasFlash: false,
            show_popover: false,
            orientationChangeTimeout: null,
            appStateChangeTimeout: null,
            setupAttempts: 0,
            maxSetupAttempts: 3,
            emitter: mitt(),
        };
    },
    mounted() {
        // For web platform, go directly to gallery picker instead of showing camera UI
        if (this.$platform === 'web') {
            // Use native file input for web platform
            this.handleWebFileSelection();
            return;
        }

        // Setup timer to detect if camera setup hangs
        const mountTimeoutId = setTimeout(() => {
            // If we're still in starting state after 5 seconds, something is wrong
            if (this.loading_state === "starting") {
                console.error("Camera setup did not complete in time, resetting");
                this.loading_state = "idle";
                this.$toast.error("Camera opstarten duurde te lang. Probeer opnieuw.");
                modalController.dismiss(null, "timeout");
            }
        }, 5000);

        // Setup handler to clear timeout on component unmount
        this.emitter.on('beforeUnmount', () => {
            clearTimeout(mountTimeoutId);
        });

        // Store timeout ID for reference
        this._mountTimeoutId = mountTimeoutId;

        this.setup();
        if (this.$platform === 'ios' && this.$store && this.$store.dispatch) {
            this.$store.dispatch('monitorAppState');
        }
    },
    beforeUnmount() {
        // Emit event before unmount for any registered listeners
        this.emitter.emit('beforeUnmount');

        // Stop camera operations
        this.stopCamera();

        // Clear any pending timeouts
        if (this.orientationChangeTimeout) {
            clearTimeout(this.orientationChangeTimeout);
        }
        if (this.appStateChangeTimeout) {
            clearTimeout(this.appStateChangeTimeout);
        }

        // CRITICAL: Clear image_urls array to free memory
        // This array holds full base64 images (3MB+ each) in reactive Vue state
        // Without this, the base64 strings stay in memory after modal closes
        this.image_urls = [];

        // Clean up emitter
        this.emitter.all.clear();
    },
    methods: {
        // Web-specific file input handler
        async handleWebFileSelection() {
            try {
                // Create a file input element
                const input = document.createElement('input');
                input.type = 'file';
                input.accept = 'image/*';
                input.multiple = true;
                
                // Handle file selection
                input.onchange = async (event) => {
                    const files = event.target.files;
                    if (!files || files.length === 0) {
                        modalController.dismiss(null, "cancel");
                        return;
                    }
                    
                    this.loading_state = "loading";
                    
                    // Process each selected file
                    for (const file of files) {
                        try {
                            // Read file as base64
                            const base64 = await this.readFileAsBase64(file);
                            
                            this.image_urls.push({
                                base64,
                                uuid: uuidv4(),
                                imageable_uuid: this.imageable_uuid,
                                created_at: new Date().toISOString(),
                                format: file.type || 'image/jpeg',
                            });
                        } catch (error) {
                            console.error("Error processing file:", error);
                        }
                    }
                    
                    // Save the images
                    await this.closeAndSave();
                };
                
                // Handle cancellation
                input.oncancel = () => {
                    modalController.dismiss(null, "cancel");
                };
                
                // Trigger file selection dialog
                input.click();
            } catch (error) {
                console.error("Error in web file selection:", error);
                modalController.dismiss(null, "error");
            }
        },

        // UI control methods
        settings() {
            this.show_popover = true;
        },

        closePopover() {
            this.show_popover = false;
        },

        // Camera control methods
        async stopCamera() {
            try {
                await CameraPreview.stop();
                document.querySelector("body")?.classList.remove("camera-preview-active");
                this.loading_state = "idle";
            } catch (err) {
                console.log(err);
                document.querySelector("body")?.classList.remove("camera-preview-active");
            }
        },

        async deleteImage(image) {
            this.image_urls = this.image_urls.filter((img) => img.uuid !== image.uuid);
        },

        async setup() {
            try {
                // Track retry attempts
                this.setupAttempts++;
                console.log(`Camera setup attempt ${this.setupAttempts}/${this.maxSetupAttempts}`);

                // If we've exceeded max attempts, give up
                if (this.setupAttempts > this.maxSetupAttempts) {
                    console.error("Maximum camera setup attempts reached, giving up");
                    this.loading_state = "idle";
                    this.$toast.error("Camera kan niet worden geinitialiseerd. Probeer later opnieuw.");
                    modalController.dismiss(null, "max_attempts");
                    return;
                }

                document.querySelector("body")?.classList.add("camera-preview-active");
                document.querySelector("#camera")?.setAttribute("style", "--background: transparent");

                if (this.$platform === "web") return;

                // Check if camera is already running and stop it
                try {
                    const isRunning = await CameraPreview.isCameraStarted();
                    if (isRunning.value) {
                        await CameraPreview.stop();
                    }
                } catch (err) {
                    // Ignore "camera already stopped" errors
                    console.log("Camera stop check:", err);
                }

                // Request only camera permission (not photos/gallery permission)
                const { camera } = await Camera.requestPermissions({ permissions: ['camera'] });
                if (camera !== 'granted') {
                    this.$toast.error("Camera toegang is vereist");
                    modalController.dismiss(null, "cancel");
                    return;
                }

                // Configure and start camera
                const cameraOptions = this.getCameraOptions();
                await CameraPreview.start(cameraOptions);
                this.loading_state = "idle";
                this.setupAttempts = 0; // Reset counter on success

                // Check flash support
                await this.checkFlashSupport();

                // Safety timeout to prevent UI from hanging
                const setupTimeoutId = setTimeout(() => {
                    if (this.loading_state === "starting") {
                        console.log("Camera setup timed out, resetting state");
                        this.loading_state = "idle";

                        // If we're still on the starting state after timeout, try one more time
                        if (this.setupAttempts < this.maxSetupAttempts) {
                            console.log("Attempting to restart camera after timeout");
                            this.setup();
                        }
                    }
                }, 2000);

                // Make sure timeout is cleared if we exit early
                return () => clearTimeout(setupTimeoutId);
            } catch (err) {
                console.error("Camera setup error:", err);
                this.loading_state = "idle";

                // Try again on error if we haven't hit max attempts
                if (this.setupAttempts < this.maxSetupAttempts) {
                    console.log("Retrying camera setup after error");
                    setTimeout(() => this.setup(), 500);
                    return;
                }

                this.$toast.error("Er is een probleem met de camera. Herstart de app.");
                modalController.dismiss(null, "cancel");
            }
        },

        getCameraOptions() {
            // Base camera options
            const options = {
                parent: "cameraPreview",
                disableAudio: true,
                position: "rear",
                toBack: true,
                height: window.innerHeight,
                width: window.innerWidth,
                lockAndroidOrientation: false,
                flashMode: "off",
                enableZoom: true,
                disableExifHeaderStripping: true,
            };

            // iOS-specific options
            if (this.$platform === 'ios') {
                options.paddingBottom = 120;
                options.paddingTop = 120;
                options.toBack = true;
                options.enableHighResolution = true;
                options.disableExifHeaderStripping = true;
            }

            return options;
        },

        async checkFlashSupport() {
            try {
                const supported = await CameraPreview.getSupportedFlashModes();
                this.hasFlash = supported.result.includes("torch");

                if (this.hasFlash && this.$store.getters.getFlash) {
                    this.flashOn();
                    this.$store.commit("setFlash", true);
                }
            } catch (err) {
                console.error("Flash detection error:", err);
                // Default to having flash controls on iOS even if detection fails
                if (this.$platform === 'ios') {
                    this.hasFlash = true;
                    console.log('Assuming iOS device has flash capability');
                }
            }
        },

        async flashToggle() {
            if (this.loading_state !== "idle") return;

            try {
                const supported = await CameraPreview.getSupportedFlashModes();
                this.flashMode = this.flashMode === "off" ? "torch" : "off";

                if (supported.result.includes(this.flashMode)) {
                    await CameraPreview.setFlashMode({ flashMode: this.flashMode });
                    this.$store.commit("setFlash", this.flashMode === "torch");
                }

                this.loading_state = "idle";
            } catch (err) {
                console.error(err);
                this.loading_state = "idle";
            }
        },

        flashOn() {
            this.flashMode = "torch";
            CameraPreview.setFlashMode({ flashMode: "torch" });
        },

        async flip() {
            if (this.loading_state !== "idle") return;

            this.loading_state = "reverse";
            try {
                await CameraPreview.flip();
                this.loading_state = "idle";
            } catch (err) {
                console.log(err);
                this.loading_state = "idle";
            }
        },

        async takePicture() {
            if (this.loading_state !== "idle") return;

            this.loading_state = "picture";
            const options = { quality: 100 };

            // Show flash effect
            this.showFlash = true;
            setTimeout(() => {
                this.showFlash = false;
            }, 150);

            try {
                const result = await CameraPreview.capture(options);
                const base64Data = `data:image/jpeg;base64,${result.value}`;

                this.image_urls.push({
                    base64: base64Data,
                    uuid: uuidv4(),
                    imageable_uuid: this.imageable_uuid,
                    created_at: new Date().toISOString(),
                });
            } catch (error) {
                console.error("Error capturing picture:", error);
            } finally {
                this.loading_state = "idle";
            }
        },

        async getFromGallery() {
            if (this.loading_state === "loading") return;

            this.loading_state = "loading";
            try {
                // On Android 13+ (API 33+), Camera.pickImages uses the system photo picker
                // which doesn't require READ_MEDIA_IMAGES permission (Google Play compliant)
                const images = await Camera.pickImages({
                    quality: 100,
                    allowEditing: false,
                    correctOrientation: false,
                });

                // Process each image
                await Promise.all(images.photos.map(async (image) => {
                    try {
                        const file = await fetch(image.webPath);
                        const blob = await file.blob();

                        // Read as base64
                        const base64 = await this.readFileAsBase64(blob);

                        this.image_urls.push({
                            base64,
                            uuid: uuidv4(),
                            imageable_uuid: this.imageable_uuid,
                            created_at: new Date().toISOString(),
                            format: blob.type || 'image/jpeg',
                        });
                    } catch (error) {
                        console.error("Error processing image:", error);
                    }
                }));
            } catch (error) {
                console.error("Error picking images:", error);
                // Show user-friendly error message
                this.$toast?.error("Kan afbeeldingen niet selecteren");
            } finally {
                this.loading_state = "idle";
            }
        },

        readFileAsBase64(blob) {
            return new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.onloadend = () => resolve(reader.result);
                reader.onerror = error => reject(error);
                reader.readAsDataURL(blob);
            });
        },

        async closeAndSave() {
            if (this.loading_state === "saving") return;

            try {
                this.loading_state = "saving";

                // Use a timeout to prevent the saving state from hanging indefinitely
                const saveTimeoutId = setTimeout(() => {
                    console.error("Image saving timed out, forcing completion");
                    this.loading_state = "idle";
                    // Only dismiss if we haven't already
                    try {
                        modalController.dismiss({ error: "timeout" }, "error");
                    } catch (e) {
                        // Modal might already be dismissed
                    }
                }, 10000); // 10 seconds timeout

                // Clean up UI
                document.querySelector("body")?.classList.remove("camera-preview-active");

                // Stop camera preview (not needed on web)
                if (this.$platform !== 'web') {
                    try {
                        await CameraPreview.stop();
                    } catch (err) {
                        console.error("Error stopping camera preview:", err);
                    }
                }

                // Check if there are images to save
                if (this.image_urls.length === 0) {
                    clearTimeout(saveTimeoutId);
                    this.loading_state = "idle";
                    modalController.dismiss(null, "done");
                    return;
                }

                // Process images
                const processedImages = await this.processImagesForSaving();
                console.log(`Processing ${processedImages.length} images for saving on ${this.$platform}`);

                // Store images locally before upload for immediate visibility in the UI
                try {
                    await db.images.bulkPut(processedImages);
                    console.log(`Saved ${processedImages.length} images to local database`);
                    // Trigger component count refresh to update image icon
                    this.$store.dispatch('setComponentCountKey');
                } catch (err) {
                    console.error("Error saving images locally to Dexie:", err);
                }

                // Save to local file system if enabled (iOS/Android only)
                if (this.$platform !== 'web' && this.$store.getters.getImageSaveLocalBackup) {
                    try {
                        await imageFileManager.saveMultipleImages(processedImages);
                        console.log('Images backed up to local file system');
                    } catch (error) {
                        console.error('Error saving images to local file system:', error);
                        // Don't fail the whole process if local backup fails
                    }
                }

                // To avoid blocking the UI, we'll dismiss the modal now and let compression 
                // continue in the background. The user will see images right away.
                // Return processed images to parent so UI can update immediately
                clearTimeout(saveTimeoutId); // Clear timeout since we're about to successfully dismiss
                modalController.dismiss({ images: processedImages }, "done");

                // Save images - after the modal is dismissed to improve user experience
                try {
                    await syncApi.compressAndSaveImages(
                        processedImages,
                        this.upload_url,
                        this.imageable_uuid,
                        this.$store.getters.getImageTimestamp,
                        this.$store.getters.getImageInspectorName,
                        this.$store.getters.getUser.name,
                        this.$store.getters.checkSetting("crop_to_square")
                    );

                    // Save to gallery if enabled
                    if (this.$store.getters.getImageSaveInGallery) {
                        await this.saveToGallery();
                    }
                } catch (compressionError) {
                    console.error("Error during background image processing:", compressionError);
                    // We don't show toast here since modal is already dismissed
                }
            } catch (error) {
                console.error("Error in closeAndSave:", error);
                this.$toast?.error(error);
                this.loading_state = "idle";
                modalController.dismiss(null, "error");
            }
        },

        async processImagesForSaving() {
            // Check orientation for each image
            for (let image of this.image_urls) {
                try {
                    image.orientation = await this.checkImageOrientation(image.base64);
                } catch (error) {
                    console.warn(`Error detecting orientation:`, error);
                    image.orientation = "unknown";
                }
            }

            // Prepare images for saving with upload status
            return this.image_urls.map(image => ({
                base64: image.base64,
                uuid: image.uuid,
                imageable_uuid: image.imageable_uuid,
                created_at: image.created_at,
                orientation: image.orientation || "unknown",
                updated_at: new Date().toISOString(),
                compressed: false, // Mark as not compressed initially
                upload_status: 'pending', // Initial upload status for S3
                sync_status: 'pending', // Initial sync status
            }));
        },

        async checkImageOrientation(base64) {
            return new Promise((resolve, reject) => {
                const img = new Image();
                img.onload = () => {
                    if (img.width > img.height) resolve("landscape");
                    else if (img.height > img.width) resolve("portrait");
                    else resolve("square");
                };
                img.onerror = error => reject(error);
                img.src = base64;
            });
        },

        async saveToGallery() {
            try {
                // Get images from database
                const images = await db.images.where("imageable_uuid").equals(this.imageable_uuid).toArray();

                // Save each image
                for (let image of images) {
                    try {
                        // Remove the base64 header if present
                        const base64Data = image.base64.includes(",") ? image.base64.split(",")[1] : image.base64;

                        if (this.$platform === 'ios') {
                            // iOS: Use Camera.savePhoto which handles permissions internally
                            // Will prompt user for permission if not already granted
                            await Camera.savePhoto({
                                path: null,
                                data: base64Data,
                                albumName: "Novacheck"
                            });
                        } else {
                            // Android: Attempt to save to Pictures directory
                            // Note: This may fail on Android 10+ due to scoped storage restrictions
                            // For Android 13+, consider using MediaStore API or user may need to manually save
                            const path = "novacheck/" + image.uuid + ".jpg";
                            await Filesystem.writeFile({
                                path,
                                data: base64Data,
                                directory: Directory.Pictures,
                                recursive: true
                            });
                        }
                    } catch (error) {
                        console.error(`Error saving image to gallery:`, error);
                        // Fail silently - user will still have images in the app
                    }
                }
            } catch (error) {
                console.error("Error saving to gallery:", error);
                // Fail silently - this is an optional feature
            }
        },

        async confirmBack() {
            this.loading_state = "saving";
            document.querySelector("body")?.classList.remove("camera-preview-active");
            await CameraPreview.stop();
            modalController.dismiss(null, "cancel");
        }
    },
    computed: {
        setTimeStamp: {
            get() {
                return this.$store.getters.getImageTimestamp;
            },
            set(value) {
                this.$store.dispatch("setImageTimestamp", value);
            },
        },
        setCoordinates: {
            get() {
                return this.$store.getters.getImageCoordinates;
            },
            set(value) {
                this.$store.dispatch("setImageCoordinates", value);
            },
        },
        setSaveInGallery: {
            get() {
                return this.$store.getters.getImageSaveInGallery;
            },
            set(value) {
                this.$store.dispatch("setImageSaveInGallery", value);
            },
        },
        setSaveLocalBackup: {
            get() {
                return this.$store.getters.getImageSaveLocalBackup;
            },
            set(value) {
                this.$store.dispatch("setImageSaveLocalBackup", value);
            },
        },
        setInspectorName: {
            get() {
                return this.$store.getters.getImageInspectorName;
            },
            set(value) {
                this.$store.dispatch("setImageInspectorName", value);
            },
        },
        screenOrientation() {
            return this.$store.getters.getOrientation;
        },
        squareStyle() {
            const topBarHeight = 150;
            const bottomBarHeight = 150;
            const availableHeight = this.$vssHeight - topBarHeight - bottomBarHeight;
            const availableWidth = this.$vssWidth;
            const squareSize = Math.min(availableWidth, availableHeight);

            return {
                width: `${squareSize}px`,
                height: `${squareSize}px`,
                margin: "auto",
            };
        },
        showCameraControls() {
            return this.$platform === 'ios' || this.$vssHeight > 400;
        },
    },
    watch: {
        screenOrientation: {
            immediate: false,
            handler() {
                if (this.$platform === 'ios') {
                    this.loading_state = "starting";
                    CameraPreview.stop().then(() => {
                        if (this.orientationChangeTimeout) {
                            clearTimeout(this.orientationChangeTimeout);
                        }
                        this.orientationChangeTimeout = setTimeout(() => {
                            // Verify component is still mounted before setup
                            if (this._isDestroyed) {
                                console.log("Component unmounted, skipping orientation camera setup");
                                return;
                            }
                            this.setup();
                        }, 500);
                    }).catch(err => {
                        console.error('Error stopping camera during orientation change:', err);
                        // Only restart if component is still mounted
                        if (!this._isDestroyed) {
                            this.loading_state = "starting";
                            this.setup();
                        }
                    });
                } else {
                    this.setup();
                }
            },
        },
        '$store.getters.appActive': {
            immediate: false,
            handler(isActive) {
                if (this.$platform === 'ios') {
                    if (isActive) {
                        this.loading_state = 'starting';
                        if (this.appStateChangeTimeout) {
                            clearTimeout(this.appStateChangeTimeout);
                        }

                        // Verify component is still mounted before starting the camera again
                        this.appStateChangeTimeout = setTimeout(() => {
                            if (this._isDestroyed) {
                                console.log("Component unmounted, skipping app state camera setup");
                                return;
                            }
                            this.setup();
                        }, 600);
                    } else {
                        this.stopCamera();
                    }
                }
            }
        }
    }
};
</script>

<style>
/* iOS-specific styles for better visibility */
.ios-button {
    box-shadow: 0 0 10px rgba(0, 0, 0, 0.5), 0 0 0 2px rgba(255, 255, 255, 0.8);
    border: 2px solid rgba(255, 255, 255, 0.8);
    z-index: 100;
    /* Ensure buttons are on top */
    background-color: rgba(0, 0, 0, 0.3);
    /* Semi-transparent background */
}

.ios-footer {
    z-index: 10;
    position: relative;
    background: rgba(0, 0, 0, 0.4);
    min-height: 80px;
    /* Ensure minimum height for smaller screens */
}

.ios-control-bar {
    background: rgba(0, 0, 0, 0.4);
    box-shadow: 0 -3px 10px rgba(0, 0, 0, 0.3);
    backdrop-filter: blur(5px);
    -webkit-backdrop-filter: blur(5px);
    padding: 10px 0;
    /* Add padding for smaller screens */
}

.image-preview-array-wrapper {
    position: absolute;
    bottom: 80px;
    left: 0;
    width: 100%;
    height: 100px;
    display: flex;
    justify-content: start;
    align-items: center;
    z-index: 1;
    overflow-x: auto;
    overflow-y: hidden;
    white-space: nowrap;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none;
    -ms-overflow-style: none;
    padding-left: 10px;
    padding-right: 10px;
}

.image-preview-array-wrapper::-webkit-scrollbar {
    display: none;
}

.image-preview-container {
    position: relative;
    display: inline-block;
    margin: 5px;
    border-radius: 5px;
    overflow: hidden;
    width: 80px;
    height: 80px;
    border: 2px solid #ccc;
}

.image-preview-container img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center;
}

.right-align-buttons {
    position: absolute;
    top: 50%;
    right: 15px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    transform: translateY(-50%);
    gap: 15px;
    z-index: 10;
}

.right-align-buttons ion-button:not(:last-child) {
    margin-bottom: 15px;
}

.loading-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: var(--ion-color-custom-white);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 999;
}

.loading-spinner,
.loading-text {
    display: flex;
    justify-content: center;
    align-items: center;
}

.absolute-center-button {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
}

.topleft {
    position: absolute;
    top: 15px;
    left: 15px;
    z-index: 10;
}

.topright {
    position: absolute;
    top: 15px;
    right: 15px;
    z-index: 10;
}

.background-on {
    background-color: rgba(0, 0, 0, 0.636) !important;
}

.background-on * {
    color: yellow !important;
}

.custom-popover {
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1000;
}

.popover-content {
    background-color: var(--ion-color-custom-white);
    border-radius: 10px;
    width: 90%;
    max-width: 400px;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.2);
    padding: 20px;
    animation: fadeIn 0.3s ease-in-out;
}

@keyframes fadeIn {
    from {
        opacity: 0;
        transform: scale(0.95);
    }

    to {
        opacity: 1;
        transform: scale(1);
    }
}
</style>
